import * as THREE from 'three';
import {volcanoDirection,volcanoActivity,createVolcanoActivity,type PlanetSampler,type TerrainSample,type VolcanoDef} from '@hyperlight/shared';

/** Static vertices on the shared liquid boundary. Only crust/emission advects;
 * the physical surface does not bob, swim, or detach from the channel walls. */
export function makeLavaMaterial(time:THREE.IUniform<number>,glow:THREE.IUniform<number>={value:0}):THREE.MeshStandardMaterial {
  const material=new THREE.MeshStandardMaterial({color:0x251a16,roughness:.94,metalness:0,side:THREE.DoubleSide});
  material.onBeforeCompile=shader=>{
    shader.uniforms.uLavaTime=time;
    shader.uniforms.uVentGlow=glow;
    shader.vertexShader='attribute vec3 aLava; varying vec3 vLava;\n'+shader.vertexShader;
    shader.vertexShader=shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvLava=aLava;');
    shader.fragmentShader=`uniform float uLavaTime,uVentGlow; varying vec3 vLava;
      float lavaHash(vec2 p){p.y=mod(p.y,24.0);return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
      float lavaFacet(vec2 p){vec2 c=floor(p),f=fract(p);float a=lavaHash(c),b=lavaHash(c+vec2(1,0)),d=lavaHash(c+vec2(0,1)),e=lavaHash(c+vec2(1,1));
        return f.x+f.y<1.0?a+(b-a)*f.x+(d-a)*f.y:e+(d-e)*(1.0-f.x)+(b-e)*(1.0-f.y);}
      float lavaHeat(){
        float flow=lavaFacet(vec2(vLava.x*.27,vLava.y*.10-uLavaTime*.04));
        float core=smoothstep(.006,.12,vLava.z);
        return core*(.045+.955*smoothstep(.48,.65,flow));
      }\n`+shader.fragmentShader;
    shader.fragmentShader=shader.fragmentShader.replace('#include <color_fragment>',`#include <color_fragment>
      float hot=lavaHeat(); diffuseColor.rgb=mix(vec3(.037,.026,.023),vec3(.22,.068,.016),hot);`);
    shader.fragmentShader=shader.fragmentShader.replace('#include <emissivemap_fragment>',`#include <emissivemap_fragment>
      float heat=lavaHeat(); totalEmissiveRadiance+=vec3(.80,.065,.003)*(heat+uVentGlow*smoothstep(.006,.12,vLava.z)*(.12+.45*heat));`);
  };
  material.customProgramCacheKey=()=> 'finite-lava-v3';
  return material;
}

export class LavaFlows {
  readonly group=new THREE.Group();
  readonly time={value:0};
  readonly material=makeLavaMaterial(this.time);
  readonly stats={volcanoes:0,meshes:0,triangles:0};
  private readonly pools:{glow:THREE.IUniform<number>;material:THREE.MeshStandardMaterial;vent:VolcanoDef}[]=[];
  private readonly activity=createVolcanoActivity();
  constructor(readonly sampler:PlanetSampler) {
    this.group.name='volcanic-lava';
    for(const v of sampler.volcanoes()) {this.buildPool(v);this.buildFlow(v);this.stats.volcanoes++;}
  }
  private mesh(v:VolcanoDef,xy:number[],along:number[],indices:number[],name:string,material=this.material):void {
    const radius=this.sampler.def.radius,origin=new THREE.Vector3(...v.origin).multiplyScalar(radius+v.baseM);
    const positions:number[]=[],fields:number[]=[],ts={} as TerrainSample;
    for(let i=0;i<xy.length/2;i++) {
      const x=xy[i*2],y=xy[i*2+1],d=volcanoDirection(v,radius,x,y);
      this.sampler.sample(...d,ts,false);
      const depth=ts.lavaDepthM??0;
      const surface=depth>0?ts.lavaSurfaceM!:ts.h*this.sampler.amplitude;
      const r=radius+surface+.02;
      positions.push(d[0]*r-origin.x,d[1]*r-origin.y,d[2]*r-origin.z);
      fields.push(along[i*2],along[i*2+1],depth);
    }
    const geo=new THREE.BufferGeometry();geo.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
    geo.setAttribute('aLava',new THREE.Float32BufferAttribute(fields,3));geo.setIndex(indices);geo.computeVertexNormals();geo.computeBoundingSphere();
    const mesh=new THREE.Mesh(geo,material);mesh.position.copy(origin);mesh.receiveShadow=true;mesh.name=name;
    this.group.add(mesh);this.stats.meshes++;this.stats.triangles+=indices.length/3;
  }
  private buildPool(v:VolcanoDef):void {
    // Concentric rings sample the actual round wet boundary; no square cap.
    const xy=[0,0],along=[0,0],indices:number[]=[],rings=12,sides=96;
    for(let ring=1;ring<=rings;ring++)for(let i=0;i<sides;i++) {
      const a=i/sides*Math.PI*2,r=v.poolRadius*ring/rings;
      xy.push(Math.cos(a)*r,Math.sin(a)*r);along.push(Math.cos(a)*r,Math.sin(a)*r);
      const current=1+(ring-1)*sides+i,next=1+(ring-1)*sides+(i+1)%sides;
      if(ring===1)indices.push(0,next,current);
      else {const prev=current-sides,pnext=next-sides;indices.push(prev,pnext,current,pnext,next,current);}
    }
    const glow={value:0},material=makeLavaMaterial(this.time,glow);this.pools.push({glow,material,vent:v});
    this.mesh(v,xy,along,indices,'lava:vent-pool',material);
  }
  private buildFlow(v:VolcanoDef):void {
    const points=v.flow,crossCount=9,subdivisions=3,chunk=24;
    for(let start=0;start<points.length-1;start+=chunk) {
      const end=Math.min(points.length-1,start+chunk),xy:number[]=[],along:number[]=[],indices:number[]=[];
      for(let row=0;row<=(end-start)*subdivisions;row++) {
        const s=start+row/subdivisions,i=Math.min(points.length-2,Math.floor(s)),t=s-i,a=points[i],b=points[i+1];
        const x=a.x+(b.x-a.x)*t,y=a.y+(b.y-a.y)*t,half=a.half+(b.half-a.half)*t,arc=a.s+(b.s-a.s)*t;
        for(let c=0;c<crossCount;c++) {
          const across=(c/(crossCount-1)*2-1)*half;
          xy.push(x+across,y);along.push(across,arc);
          if(row>0&&c>0){const k=row*crossCount+c;indices.push(k-crossCount-1,k,k-1,k-crossCount-1,k-crossCount,k);}
        }
      }
      this.mesh(v,xy,along,indices,`lava:flow:${start}`);
    }
  }
  setTime(seconds:number):void {this.time.value=((seconds%600)+600)%600;}
  setActivity(seconds:number):void {
    for(const p of this.pools){volcanoActivity(p.vent,seconds,this.activity);p.glow.value=.65*this.activity.warning+.7*this.activity.fountain;}
  }
  dispose():void {
    this.group.removeFromParent();for(const m of this.group.children)(m as THREE.Mesh).geometry.dispose();this.material.dispose();
    for(const p of this.pools)p.material.dispose();
  }
}
